import React, { useEffect, useMemo, useState } from "react";
import api from "../../api/axios";
import "./Dashboard.css";

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [monthlySales, setMonthlySales] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchDashboard = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/api/admin/dashboard");
      setStats(res.data?.stats || {});
      setMonthlySales(res.data?.charts?.monthlySales || []);
      setInventory(res.data?.inventoryByType || []);
    } catch (err) {
      console.error("Dashboard load failed", err);
      setError("Failed to load dashboard data");
      setStats({});
      setMonthlySales([]);
      setInventory([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalUnitsSold = stats?.totalUnitsSold ?? 0;
  const revenueMonth = stats?.revenueMonth ?? 0;
  const upcomingPayments = stats?.upcomingPayments ?? { count: 0, total: 0 };
  const overduePayments = stats?.overduePayments ?? { count: 0, total: 0 };

  const salesBars = useMemo(() => {
    const max = monthlySales.reduce(
      (acc, item) => Math.max(acc, item.count ?? 0),
      0
    );
    return monthlySales.map((item) => ({
      label: item.label ?? "",
      height: max > 0 ? Math.round(((item.count ?? 0) / max) * 100) : 0,
    }));
  }, [monthlySales]);

  const topInventory = useMemo(() => {
    return (inventory || []).slice(0, 4).map((item) => {
      const sold = item.sold ?? 0;
      const total = item.total ?? 0;
      const percentage = total > 0 ? Math.round((sold / total) * 100) : 0; 
      let barClass = "success";
      if (percentage > 80) barClass = "danger";
      else if (percentage > 50) barClass = "warning";
      return {
        ...item,
        percentage,
        barClass,
      };
    });
  }, [inventory]);

  return (
    <div className="dashboard-home">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <h2 className="page-title mb-0">Dashboard Overview</h2>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={fetchDashboard}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}

      {/* --- Stats Cards Row --- */}
      <div className="stats-grid">
        {/* Card 1: Total Units Sold */}
        <div className="stat-card">
          <div className="stat-icon blue-bg">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M3 3v18h18" />
              <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3" />
            </svg>
          </div>
          <div className="stat-info">
            <h4>Total Units Sold</h4>
            <div className="stat-value">{totalUnitsSold}</div>
            <span className="stat-sub text-gray">Approved sales (units)</span>
          </div>
        </div>

        {/* Card 2: Revenue This Month */}
        <div className="stat-card">
          <div className="stat-icon green-bg">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="12" y1="1" x2="12" y2="23"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
          </div>
          <div className="stat-info">
            <h4>Revenue (Month)</h4>
            <div className="stat-value">
              ${Number(revenueMonth).toLocaleString()}
            </div>
            <span className="stat-sub text-gray">Successful payments</span>
          </div>
        </div>

        {/* Card 3: Upcoming Payments */}
        <div className="stat-card">
          <div className="stat-icon purple-bg">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
          </div>
          <div className="stat-info">
            <h4>Upcoming (7 Days)</h4>
            <div className="stat-value">
              {upcomingPayments.count} Payments
            </div>
            <span className="stat-sub text-blue">
              Est: ${Number(upcomingPayments.total).toLocaleString()}
            </span>
          </div>
        </div>

        {/* Card 4: Overdue Payments */}
        <div className="stat-card">
          <div className="stat-icon red-bg">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <div className="stat-info">
            <h4>Overdue Payments</h4>
            <div className="stat-value text-red">
              {overduePayments.count}
            </div>
            <span className="stat-sub text-red">
              Due: ${Number(overduePayments.total).toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {/* --- Charts & Tables Section --- */}
      <div className="content-grid">
        {/* Monthly Sales Graph (CSS Only) */}
        <div className="content-card">
          <div className="card-header">
            <h3>Monthly Sales Graph</h3>
          </div>

          <div className="chart-wrapper">
            {/* Vertical Label */}
            <div className="y-axis-title">Sales Volume (Units)</div>

            <div className="chart-container">
              <div className="y-axis">
                <span>100M</span>
                <span>75M</span>
                <span>50M</span>
                <span>25M</span>
                <span>10M</span>
                <span>1M</span>
                <span>0</span>
              </div>

              <div className="bars-container">
                {salesBars.map((data, index) => (
                  <div key={index} className="bar-group">
                    <div
                      className="bar"
                      style={{ height: `${data.height}%` }}
                    ></div>
                    <span className="x-label">{data.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Horizontal Label */}
            <div className="x-axis-title">Months of the Year</div>
          </div>
        </div>
        {/* Inventory Overview */}
        <div className="content-card">
          <div className="card-header">
            <h3>Inventory Overview</h3>
            <span className="text-gray small">Real-time stock</span>
          </div>
          <div className="inventory-list">
            {topInventory.length === 0 ? (
              <div className="text-muted small">No inventory data</div>
            ) : (
              topInventory.map((item, index) => (
                <div key={index} className="inventory-item">
                  <div className="inv-info">
                    <span className="inv-name">{item.type}</span>
                    <span className="inv-stats">
                      {item.sold}/{item.total} Sold
                    </span>
                  </div>
                  <div className="progress-bg">
                    <div
                      className={`progress-fill ${item.barClass}`}
                      style={{ width: `${item.percentage}%` }}
                    ></div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
